/**
 * OrganizationAvatar - Universal component for displaying organization avatars
 *
 * Displays organization logo or default icon with a type badge indicator.
 * Ensures consistent styling and behavior across the application.
 */

import {
  getOrganizationType,
  type OrganizationType,
} from "@/lib/utils/organization";
import { cn } from "@repo/ui/lib/utils";
import { Building2, UserStar } from "lucide-react";

interface OrganizationAvatarProps {
  organization: {
    logo?: string | null;
    name: string;
    metadata?: string | null;
  };
  size?: "sm" | "md" | "lg";
  showBadge?: boolean;
  className?: string;
}

/**
 * Get icon component for organization type
 */
function getOrgIconForType(type: OrganizationType) {
  return type === "agency" ? Building2 : UserStar;
}

const sizeClasses = {
  sm: {
    container: "size-6",
    logo: "size-4",
    icon: "size-4",
    badge: "size-3.5",
    badgeIcon: "size-2.25",
  },
  md: {
    container: "size-8",
    logo: "size-4",
    icon: "size-4",
    badge: "size-4",
    badgeIcon: "!size-2.5",
  },
  lg: {
    container: "size-12",
    logo: "size-8",
    icon: "size-8",
    badge: "size-5",
    badgeIcon: "size-3",
  },
};

export function OrganizationAvatar({
  organization,
  size = "md",
  showBadge = true,
  className,
}: OrganizationAvatarProps) {
  const orgType = getOrganizationType(organization.metadata);
  const Icon = getOrgIconForType(orgType);
  const sizes = sizeClasses[size];

  // Light mode: colored backgrounds, Dark mode: primary for agency
  const badgeStyles =
    orgType === "agency"
      ? "bg-primary text-primary-foreground border-primary"
      : "bg-gray-100 border-gray-200 dark:bg-background dark:border-border dark:ring-1 dark:ring-gray-800";

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-md border",
        sizes.container,
        "bg-white dark:bg-background",
        className,
      )}
    >
      {organization.logo ? (
        <img
          src={organization.logo}
          alt={organization.name}
          className={cn("rounded", sizes.logo)}
        />
      ) : (
        <Icon className={sizes.icon} />
      )}

      {showBadge && (
        <div
          className={cn(
            "absolute -bottom-1 -right-1 flex items-center justify-center rounded-full border",
            sizes.badge,
            badgeStyles,
          )}
        >
          <Icon className={sizes.badgeIcon} />
        </div>
      )}
    </div>
  );
}
